/**
 * Action Plan history / streak view (SYR-68, Phase 5).
 *
 * Reads the archived ("cleared") plays back as a record of completed plans.
 * Plays cleared together share an `archivedAt` and form one completed plan, so
 * we group by it into a newest-first timeline and surface a few gamification
 * stats — plans cleared, plays shipped, tasks done, and a consecutive-week
 * completion streak.
 */

import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { HistoryOutlined, FireOutlined } from '@ant-design/icons';
import type { IMarketerActionPlanPlay } from '../../../services/nestjs-base-api';
import { Card } from '../atoms';
import { MarketerLoading } from '../atoms/MarketerLoading';
import { THEME } from '../config/theme';
import type { Recommendation } from './RecommendationCard';

const FIX = '#DC2626';
const WIN = '#16A34A';

/** Monday-aligned 7-day bucket index (epoch day 0 = Thursday → +3 aligns Monday). */
function weekKey(d: Date): number {
  const dayIndex = Math.floor(d.getTime() / 86_400_000);
  return Math.floor((dayIndex + 3) / 7);
}

/** Consecutive-week completion streak ending at the current (or, as grace, prior) week. */
function computeStreak(dates: Date[]): number {
  if (dates.length === 0) return 0;
  const weeks = new Set(dates.map(weekKey));
  const current = weekKey(new Date());
  let start: number | null = null;
  if (weeks.has(current)) start = current;
  else if (weeks.has(current - 1)) start = current - 1;
  if (start === null) return 0;
  let streak = 0;
  for (let w = start; weeks.has(w); w -= 1) streak += 1;
  return streak;
}

interface CompletedPlan {
  archivedAt: string;
  plays: { card: Recommendation; taskCount: number }[];
  taskCount: number;
}

export interface ActionPlanHistoryProps {
  plays: IMarketerActionPlanPlay[] | undefined;
  isLoading: boolean;
}

export const ActionPlanHistory: React.FunctionComponent<ActionPlanHistoryProps> = ({
  plays,
  isLoading,
}) => {
  const intl = useIntl();

  // Group archived plays into completed plans (by shared archivedAt), newest first.
  const { plans, planCount, playCount, taskCount, streak } = useMemo(() => {
    const groups = new Map<string, CompletedPlan>();
    let tasks = 0;
    (plays ?? []).forEach((p) => {
      const key = p.archivedAt ?? '';
      const card = (p.snapshotJson ?? {}) as Recommendation;
      const n = (card.objectives ?? []).length;
      tasks += n;
      const grp = groups.get(key) ?? { archivedAt: key, plays: [], taskCount: 0 };
      grp.plays.push({ card, taskCount: n });
      grp.taskCount += n;
      groups.set(key, grp);
    });
    const orderedPlans = Array.from(groups.values()).sort((a, b) =>
      b.archivedAt.localeCompare(a.archivedAt),
    );
    const dates = orderedPlans
      .map((g) => (g.archivedAt ? new Date(g.archivedAt) : null))
      .filter((d): d is Date => !!d && !Number.isNaN(d.getTime()));
    return {
      plans: orderedPlans,
      planCount: orderedPlans.length,
      playCount: (plays ?? []).length,
      taskCount: tasks,
      streak: computeStreak(dates),
    };
  }, [plays]);

  if (isLoading) return <MarketerLoading />;

  if (planCount === 0) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '56px 24px' }}>
          <HistoryOutlined style={{ fontSize: 40, color: '#CBD5E1' }} />
          <h2
            style={{
              fontSize: 22,
              fontWeight: 600,
              color: THEME.textBase,
              letterSpacing: '-0.02em',
              margin: '16px 0 8px',
            }}
          >
            <FormattedMessage
              id="marketerView.history.emptyTitle"
              defaultMessage="No completed plans yet"
            />
          </h2>
          <p style={{ fontSize: 15, color: THEME.textSecondary, margin: '0 auto', maxWidth: 460 }}>
            <FormattedMessage
              id="marketerView.history.emptyBody"
              defaultMessage="Finish every move in a plan and clear it. Your completed plans will collect here."
            />
          </p>
        </div>
      </Card>
    );
  }

  const stats: { label: React.ReactNode; value: number; accent?: boolean }[] = [
    {
      label: <FormattedMessage id="marketerView.history.statPlans" defaultMessage="Plans cleared" />,
      value: planCount,
    },
    {
      label: <FormattedMessage id="marketerView.history.statPlays" defaultMessage="Plays shipped" />,
      value: playCount,
    },
    {
      label: <FormattedMessage id="marketerView.history.statTasks" defaultMessage="Tasks done" />,
      value: taskCount,
    },
  ];

  return (
    <div>
      {/* Stats */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: 14,
          marginBottom: 20,
        }}
      >
        {stats.map((s, i) => (
          <div
            key={i}
            style={{
              background: THEME.bgCard,
              border: `1px solid ${THEME.border}`,
              borderRadius: 14,
              padding: '16px 18px',
              boxShadow: THEME.cardShadow,
            }}
          >
            <div style={{ fontSize: 28, fontWeight: 800, color: THEME.textBase, lineHeight: 1.1 }}>
              {s.value}
            </div>
            <div style={{ fontSize: 13, color: THEME.textMuted, marginTop: 4 }}>{s.label}</div>
          </div>
        ))}
        {/* Streak tile — highlighted when alive. */}
        <div
          style={{
            background:
              streak > 0 ? 'linear-gradient(135deg, #FFF7ED 0%, #FFEDD5 100%)' : THEME.bgCard,
            border: `1px solid ${streak > 0 ? '#FDBA74' : THEME.border}`,
            borderRadius: 14,
            padding: '16px 18px',
            boxShadow: THEME.cardShadow,
          }}
        >
          <div
            style={{
              fontSize: 28,
              fontWeight: 800,
              color: streak > 0 ? '#C2410C' : THEME.textBase,
              lineHeight: 1.1,
              display: 'flex',
              alignItems: 'center',
              gap: 7,
            }}
          >
            <FireOutlined style={{ fontSize: 22, color: streak > 0 ? '#EA580C' : '#CBD5E1' }} />
            {streak}
          </div>
          <div style={{ fontSize: 13, color: streak > 0 ? '#9A3412' : THEME.textMuted, marginTop: 4 }}>
            <FormattedMessage
              id="marketerView.history.statStreak"
              defaultMessage="{n, plural, one {week streak} other {week streak}}"
              values={{ n: streak }}
            />
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {plans.map((plan, idx) => {
          const when = plan.archivedAt ? new Date(plan.archivedAt) : null;
          const dateLabel =
            when && !Number.isNaN(when.getTime())
              ? intl.formatDate(when, { day: 'numeric', month: 'short', year: 'numeric' })
              : '';
          return (
            <Card key={plan.archivedAt || idx}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 10,
                  marginBottom: 14,
                  flexWrap: 'wrap',
                }}
              >
                <span style={{ fontSize: 15, fontWeight: 700, color: THEME.textBase }}>
                  {dateLabel}
                </span>
                <span style={{ fontSize: 13, color: THEME.textMuted }}>
                  <FormattedMessage
                    id="marketerView.history.planSummary"
                    defaultMessage="{plays} {plays, plural, one {play} other {plays}} · {tasks} {tasks, plural, one {task} other {tasks}}"
                    values={{ plays: plan.plays.length, tasks: plan.taskCount }}
                  />
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {plan.plays.map((pl, i) => {
                  const accent = pl.card.type === 'win' ? WIN : FIX;
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          letterSpacing: '0.04em',
                          color: '#fff',
                          padding: '2px 8px',
                          borderRadius: 6,
                          background: accent,
                          flexShrink: 0,
                        }}
                      >
                        {pl.card.type === 'win' ? (
                          <FormattedMessage id="marketerView.recos.quickWinShort" defaultMessage="WIN" />
                        ) : (
                          <FormattedMessage id="marketerView.recos.fixShort" defaultMessage="FIX" />
                        )}
                      </span>
                      <span style={{ fontSize: 14, fontWeight: 600, color: '#0F172A' }}>
                        {pl.card.title}
                      </span>
                    </div>
                  );
                })}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};
